// src/components/DiscountModal.jsx

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export default function DiscountModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Mostrar modal 30 segundos después de que el usuario llega a la página
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 30000); // 30 segundos

    return () => clearTimeout(timer);
  }, []);

  // Prevenir scroll cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor ingresa un email válido');
      setLoading(false);
      return;
    }

    try {
      // AQUÍ ES DONDE n8n ENTRA EN JUEGO
      // Enviamos los datos a nuestro webhook de n8n
      const response = await fetch(
        'https://qrtech.app.n8n.cloud/webhook/6cab23cb-86b1-4583-9676-93d9c3a96d23', // Reemplazar con tu URL
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
            tipo_evento: 'descuento_15_dolares',
            timestamp: new Date().toISOString(),
            source: 'modal_popup',
            pagina: window.location.href,
          }),
        }
      );

      if (response.ok) {
        setSubmitted(true);
        setEmail('');
        
        // Cerrar modal después de 3 segundos
        setTimeout(() => {
          setIsOpen(false);
          setSubmitted(false);
        }, 3000);
      } else {
        setError('Error al procesar tu solicitud. Intenta nuevamente.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Hubo un error. Por favor intenta más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSubmitted(false);
    setEmail('');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Fondo oscuro (backdrop) */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal centrado */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl max-w-sm w-full overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          {/* Header con botón cerrar */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 id="modal-title" className="text-xl font-bold text-white">
              🎁 ¡Descuento Especial!
            </h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded-lg transition"
              aria-label="Cerrar modal"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Contenido */}
          <div className="p-6">
            {!submitted ? (
              <>
                {/* Oferta destacada */}
                <div className="mb-4">
                  <div className="bg-emerald-600/20 border border-emerald-500/30 rounded-lg p-4 mb-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-emerald-300">
                        -$15 USD
                      </div>
                      <div className="text-sm text-white/70 mt-1">
                        En la compra de tu próximo celular
                      </div>
                    </div>
                  </div>

                  <p className="text-white/80 text-sm leading-relaxed">
                    Ingresa tu email y te enviaremos un código especial para
                    obtener{' '}
                    <strong className="text-emerald-300">$15 USD de descuento</strong>{' '}
                    en la compra de cualquier equipo.
                  </p>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full h-11 rounded-lg border border-white/10 bg-white/5 px-4 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/60 focus:border-emerald-400/30 transition"
                      disabled={loading}
                      required
                    />
                  </div>

                  {/* Mensaje de error */}
                  {error && (
                    <div className="text-sm text-red-400 bg-red-900/20 border border-red-500/20 rounded-lg p-2 px-3">
                      {error}
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-white transition"
                  >
                    {loading ? 'Procesando...' : 'Obtener Código de Descuento'}
                  </button>

                  {/* Microcopy */}
                  <p className="text-xs text-white/50 text-center">
                    Nunca compartiremos tu email. Solo descuentos exclusivos.
                  </p>
                </form>
              </>
            ) : (
              /* Mensaje de éxito */
              <div className="text-center py-8">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  ¡Listo!
                </h3>
                <p className="text-white/70 text-sm">
                  Revisa tu email en los próximos minutos. Te enviaremos tu código
                  especial de descuento.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
